import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Header.scss';
import logoImg from '../../assets/images/logo.png';
import { FiMenu, FiX } from 'react-icons/fi';

const menuData = [
  {
    label: '회사소개',
    path: '/about',
    children: [
      { label: '인사말', path: '/about/greeting' },
      { label: '찾아오시는 길', path: '/about/map' },
      { label: '회사소개서', path: '/about/intro' },
    ],
  },
  {
    label: '생산',
    path: '/production',
    children: [
      { label: '보유설비', path: '/production/facilities' },
      { label: '제조공정', path: '/production/process' },
    ],
  },
  {
    label: '제품소개',
    path: '/products',
    children: [
      {
        label: '양면테이프',
        path: '/products/double-sided',
        children: [
          { label: '화지양면', path: '/products/double-sided/whaja' },
          { label: '핫멜트양면', path: '/products/double-sided/hotmelt' },
          { label: 'PET90 양면', path: '/products/double-sided/pet90' },
          { label: '크라프트양면', path: '/products/double-sided/kraft' },
          { label: '인쇄양면 테이프', path: '/products/double-sided/crown' },
        ],
      },
      {
        label: '산업용 테이프',
        path: '/products/industrial',
        children: [
          { label: '마스킹테이프', path: '/products/industrial/masking' },
          { label: '라인 테이프', path: '/products/industrial/line' },
        ],
      },
      {
        label: '폼테이프',
        path: '/products/foam',
        children: [
          { label: 'EVA폼 테이프', path: '/products/foam/eva' },
          { label: 'PE폼 테이프', path: '/products/foam/pe' },
        ],
      },
    ],
  },
  { label: '샘플요청', path: '/request-sample' },
  { label: '견적요청', path: '/request-quote' },
  {
    label: '커뮤니티',
    path: '/community',
    children: [
      { label: '공지', path: '/community/notice' },
      { label: '발주관리(준비중)', path: '/community/orders' },
      { label: '인증서 및 성적서', path: '/community/cert' },
    ],
  },
];

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isScroll, setIsScroll] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDesktopMenu, setActiveDesktopMenu] = useState(null);
  const [sitemapOpen, setSitemapOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScroll(window.scrollY > 30);
    const handleResize = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileMenuOpen(false);
        setActiveDesktopMenu(null);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDesktopMenu(null);
    setSitemapOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen || sitemapOpen ? 'hidden' : '';
  }, [mobileMenuOpen, sitemapOpen]);

  const getFirstChildPath = (menu) => {
    if (menu.children && menu.children.length > 0) {
      return getFirstChildPath(menu.children[0]);
    }
    return menu.path;
  };

  const goTo = (path) => {
    if (path === '/community/orders') {
      navigate('/community/orders');
      setMobileMenuOpen(false);
      setSitemapOpen(false);
      return;
    }

    const findMenuByPath = (items, targetPath) => {
      for (const item of items) {
        if (item.path === targetPath) return item;
        if (item.children) {
          const found = findMenuByPath(item.children, targetPath);
          if (found) return found;
        }
      }
      return null;
    };

    const targetMenu = findMenuByPath(menuData, path);
    if (targetMenu) {
      const finalPath = getFirstChildPath(targetMenu);
      navigate(finalPath);
    } else {
      navigate(path);
    }
    setMobileMenuOpen(false);
    setSitemapOpen(false);
  };

  const isIncludedPath = (path) => {
    if (!path || path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const renderDesktopDropdown = () => {
    if (activeDesktopMenu === null) return null;
    const currentMenu = menuData[activeDesktopMenu];
    if (!currentMenu?.children?.length) return null;

    return (
      <div className="mega-dropdown">
        <div className="mega-dropdown__container">
          <div className="mega-dropdown__info">
            <span className="category-tag">CATEGORY</span>
            <h2 className="category-title">{currentMenu.label}</h2>
            <p className="category-desc">BEST TRADING의 전문적인 솔루션을 확인해보세요.</p>
          </div>
          <div className="mega-dropdown__content">
            {currentMenu.children.map((depth2) => (
              <div key={depth2.label} className="menu-column">
                <button className="depth2-title" onClick={() => goTo(depth2.path)}>
                  {depth2.label}
                </button>
                {depth2.children && (
                  <ul className="depth3-list">
                    {depth2.children.map((depth3) => (
                      <li key={depth3.label}>
                        <button className="depth3-link" onClick={() => goTo(depth3.path)}>
                          {depth3.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <header
      className={`header ${isScroll ? 'is-scroll' : ''} ${mobileMenuOpen ? 'is-m-open' : ''} ${
        activeDesktopMenu !== null ? 'is-hover' : ''
      }`}
    >
      <div className="header__inner">
        <h1
          className="logo"
          onClick={() => {
            navigate('/');
            setMobileMenuOpen(false);
          }}
        >
          <img src={logoImg} alt="BEST TRADING" />
        </h1>

        {!isMobile && (
          <nav className="desktop-nav">
            <ul className="gnb">
              {menuData.map((menu, index) => (
                <li
                  key={menu.label}
                  className={`depth1-item ${isIncludedPath(menu.path) ? 'active' : ''}`}
                  onMouseEnter={() => setActiveDesktopMenu(menu.children ? index : null)}
                >
                  <button className="depth1-btn" onClick={() => goTo(menu.path)}>
                    {menu.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        )}

        <div className="header-util">
          {!isMobile && (
            <button className="sitemap-btn" onClick={() => setSitemapOpen(true)}>
              <FiMenu size={28} />
            </button>
          )}
          {isMobile && (
            <button
              className={`m-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle Menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          )}
        </div>
      </div>

      {!isMobile && renderDesktopDropdown()}

      {/* [SITEMAP PANEL] */}
      <div className={`sitemap-panel ${sitemapOpen ? 'is-open' : ''}`}>
        <div className="sitemap-panel__header">
          <div className="sitemap-logo" onClick={() => navigate('/')}>
            <img src={logoImg} alt="BEST TRADING" />
          </div>
          <button className="sitemap-close" onClick={() => setSitemapOpen(false)}>
            <FiX />
          </button>
        </div>
        <div className="sitemap-panel__content">
          <div className="sitemap-aside">
            <div className="aside-txt">
              <span className="tag">PREMIUM ADHESIVE</span>
              <h2>
                SOLUTIONS FOR
                <br />
                EVERY INDUSTRY
              </h2>
              <p>
                20년 노하우의 정밀 가공 기술로
                <br />
                최상의 품질을 약속합니다.
              </p>
            </div>
            <div className="aside-contact">
              <p>
                <strong>TEL.</strong> 031-366-0666
              </p>
              <p>
                <strong>MAIL.</strong> oy7112@gmail.com
              </p>
            </div>
          </div>
          <div className="sitemap-grid">
            {menuData.map((menu) => (
              <div key={menu.label} className="sitemap-col">
                <h3 className="sitemap-depth1" onClick={() => goTo(menu.path)}>
                  {menu.label}
                </h3>
                <div className="sitemap-depth2-wrap">
                  {menu.children?.map((depth2) => (
                    <div key={depth2.label} className="sitemap-depth2-item">
                      <h4 className="sitemap-depth2" onClick={() => goTo(depth2.path)}>
                        {depth2.label}
                      </h4>
                      <ul className="sitemap-depth3-list">
                        {depth2.children?.map((depth3) => (
                          <li key={depth3.label} onClick={() => goTo(depth3.path)}>
                            {depth3.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* [MOBILE PANEL] */}
      <div className={`mobile-panel ${mobileMenuOpen ? 'is-open' : ''}`}>
        <div className="mobile-panel__inner">
          <ul className="m-gnb">
            {menuData.map((menu) => (
              <li key={menu.label} className="m-depth1-item">
                <button className="m-depth1-btn" onClick={() => goTo(menu.path)}>
                  {menu.label}
                </button>
                {menu.children && (
                  <ul className="m-depth2-list">
                    {menu.children.map((sub) => (
                      <li key={sub.label} onClick={() => goTo(sub.path)}>
                        {sub.label}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
          <div className="m-footer">
            <p>
              <strong>TEL.</strong> 031-366-0666
            </p>
            <p>oy7112@gmail.com</p>
          </div>
        </div>
      </div>

      <div
        className={`header-overlay ${activeDesktopMenu !== null ? 'is-visible' : ''}`}
        onMouseEnter={() => setActiveDesktopMenu(null)}
      />
    </header>
  );
};

export default Header;
